import { useCallback, useEffect, useRef, useState } from 'react';
import type { Content } from '@vivport/backend/types';
import { discardMessageDraft, sendMessageDraft, updateMessageDraft } from '@/api/content-api';
import { useContentDetail } from '@/hooks/useContentQueries';
import { useViewContextStore } from '@/store/viewContextStore';

export interface DraftFields {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  authoredBody: string;
}

export const EMPTY_DRAFT_FIELDS: DraftFields = { to: '', cc: '', bcc: '', subject: '', authoredBody: '' };

const AUTOSAVE_IDLE_MS = 1500;

export function splitRecipients(value: string): string[] {
  return Array.from(
    new Set(
      value
        .split(/[,\n;]/)
        .map((entry) => entry.trim().toLowerCase())
        .filter((entry) => entry.length > 0)
    )
  );
}

export function draftFieldsEqual(a: DraftFields, b: DraftFields): boolean {
  return (
    splitRecipients(a.to).join(',') === splitRecipients(b.to).join(',') &&
    splitRecipients(a.cc).join(',') === splitRecipients(b.cc).join(',') &&
    splitRecipients(a.bcc).join(',') === splitRecipients(b.bcc).join(',') &&
    a.subject.trim() === b.subject.trim() &&
    a.authoredBody.trim() === b.authoredBody.trim()
  );
}

export function isMeaningfulDraft(fields: DraftFields, baseline: DraftFields): boolean {
  return !draftFieldsEqual(fields, baseline);
}

export function draftFieldsFromLists(input: {
  to: string[];
  cc: string[];
  bcc: string[];
  subject: string;
  authoredBody: string;
}): DraftFields {
  return {
    to: input.to.join(', '),
    cc: input.cc.join(', '),
    bcc: input.bcc.join(', '),
    subject: input.subject,
    authoredBody: input.authoredBody,
  };
}

export interface MessageDraftLifecycleInput {
  flushKey: string;
  active: boolean;
  fields: DraftFields;
  baseline: DraftFields;
  draftContentId: number | null;
  dirty: boolean;
  onBound: (draftContentId: number) => void;
  onDirtyChange: (dirty: boolean) => void;
  onReplaceFields: (fields: DraftFields) => void;
  create: (fields: DraftFields) => Promise<{ draftContentId: number; fields: DraftFields }>;
  readRemoteFields: (content: Content) => DraftFields | null;
}

export interface MessageDraftRevision {
  pending: DraftFields | null;
  useRemote: () => void;
  keepMine: () => void;
}

export interface MessageDraftLifecycle {
  noteEdit: () => void;
  saveNow: () => void;
  flush: () => Promise<number | null>;
  send: () => Promise<number>;
  close: () => Promise<'kept' | 'discarded' | 'closed'>;
  revision: MessageDraftRevision;
  isSaving: boolean;
}

export function useMessageDraftLifecycle(input: MessageDraftLifecycleInput): MessageDraftLifecycle {
  const inputRef = useRef(input);
  inputRef.current = input;
  const savedRef = useRef<DraftFields | null>(null);
  const createInFlightRef = useRef<Promise<number> | null>(null);
  const saveChainRef = useRef<Promise<void>>(Promise.resolve());
  const timerRef = useRef<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [pending, setPending] = useState<DraftFields | null>(null);

  const clearTimer = useCallback(() => {
    if (timerRef.current !== null) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const ensureBound = useCallback(async (): Promise<number | null> => {
    const current = inputRef.current;
    if (current.draftContentId !== null) return current.draftContentId;
    if (!isMeaningfulDraft(current.fields, current.baseline)) return null;
    if (createInFlightRef.current === null) {
      const fieldsAtCreate = current.fields;
      createInFlightRef.current = (async () => {
        const created = await current.create(fieldsAtCreate);
        savedRef.current = created.fields;
        inputRef.current.onBound(created.draftContentId);
        if (draftFieldsEqual(inputRef.current.fields, fieldsAtCreate)) {
          inputRef.current.onReplaceFields(created.fields);
          inputRef.current.onDirtyChange(false);
        } else {
          inputRef.current.onDirtyChange(true);
        }
        return created.draftContentId;
      })().finally(() => {
        createInFlightRef.current = null;
      });
    }
    return createInFlightRef.current;
  }, []);

  const runSave = useCallback(async (draftContentId: number): Promise<void> => {
    const fields = inputRef.current.fields;
    if (savedRef.current !== null && draftFieldsEqual(savedRef.current, fields)) {
      inputRef.current.onDirtyChange(false);
      return;
    }
    setIsSaving(true);
    try {
      const result = await updateMessageDraft(draftContentId, {
        to: splitRecipients(fields.to),
        cc: splitRecipients(fields.cc),
        bcc: splitRecipients(fields.bcc),
        subject: fields.subject,
        authoredBody: fields.authoredBody,
      });
      savedRef.current = draftFieldsFromLists(result);
      inputRef.current.onDirtyChange(!draftFieldsEqual(inputRef.current.fields, fields));
    } finally {
      setIsSaving(false);
    }
  }, []);

  // One save at a time per lifecycle: a flush or autosave that arrives mid-save waits for
  // it, then re-checks the latest fields against what was last persisted.
  const save = useCallback(
    (draftContentId: number): Promise<void> => {
      const next = saveChainRef.current.then(() => runSave(draftContentId));
      saveChainRef.current = next.catch(() => undefined);
      return next;
    },
    [runSave]
  );

  const flush = useCallback(async (): Promise<number | null> => {
    if (!inputRef.current.active) return inputRef.current.draftContentId;
    clearTimer();
    const draftContentId = await ensureBound();
    if (draftContentId === null) return null;
    inputRef.current = { ...inputRef.current, draftContentId };
    await save(draftContentId);
    return draftContentId;
  }, [clearTimer, ensureBound, save]);

  const noteEdit = useCallback(() => {
    clearTimer();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      void flush();
    }, AUTOSAVE_IDLE_MS);
  }, [clearTimer, flush]);

  const saveNow = useCallback(() => {
    void flush();
  }, [flush]);

  const send = useCallback(async (): Promise<number> => {
    const draftContentId = await flush();
    if (draftContentId === null) {
      throw new Error('Nothing to send: the draft has no content');
    }
    await sendMessageDraft(draftContentId);
    return draftContentId;
  }, [flush]);

  const close = useCallback(async (): Promise<'kept' | 'discarded' | 'closed'> => {
    clearTimer();
    const current = inputRef.current;
    const meaningful = isMeaningfulDraft(current.fields, current.baseline);
    if (current.draftContentId === null) {
      if (!meaningful) return 'closed';
      const created = await ensureBound();
      if (created === null) return 'closed';
      inputRef.current = { ...inputRef.current, draftContentId: created };
      await save(created);
      return 'kept';
    }
    if (!meaningful) {
      await saveChainRef.current;
      await discardMessageDraft(current.draftContentId);
      savedRef.current = inputRef.current.fields;
      return 'discarded';
    }
    await save(current.draftContentId);
    return 'kept';
  }, [clearTimer, ensureBound, save]);

  const { flushKey, active, draftContentId } = input;

  useEffect(() => {
    if (!active) return;
    const store = useViewContextStore.getState();
    store.registerFlush(flushKey, async () => {
      await flush();
    });
    return () => {
      useViewContextStore.getState().unregisterFlush(flushKey);
      const latest = inputRef.current;
      if (
        latest.draftContentId !== null &&
        (savedRef.current === null || !draftFieldsEqual(savedRef.current, latest.fields))
      ) {
        void save(latest.draftContentId);
      }
    };
  }, [active, flushKey, flush, save]);

  useEffect(() => {
    if (draftContentId === null) {
      savedRef.current = null;
      setPending(null);
    }
  }, [draftContentId]);

  useEffect(() => clearTimer, [clearTimer]);

  const detailQuery = useContentDetail(draftContentId ?? 0, { enabled: draftContentId !== null });
  const remoteContent = detailQuery.data;

  useEffect(() => {
    if (remoteContent === undefined || inputRef.current.draftContentId === null) return;
    const remote = inputRef.current.readRemoteFields(remoteContent);
    if (remote === null) return;
    if (savedRef.current !== null && draftFieldsEqual(savedRef.current, remote)) return;
    if (!inputRef.current.dirty) {
      savedRef.current = remote;
      inputRef.current.onReplaceFields(remote);
      setPending(null);
      return;
    }
    setPending(remote);
  }, [remoteContent]);

  const useRemote = useCallback(() => {
    if (pending === null) return;
    savedRef.current = pending;
    inputRef.current.onReplaceFields(pending);
    inputRef.current.onDirtyChange(false);
    setPending(null);
  }, [pending]);

  const keepMine = useCallback(() => {
    setPending(null);
  }, []);

  return {
    noteEdit,
    saveNow,
    flush,
    send,
    close,
    revision: { pending, useRemote, keepMine },
    isSaving,
  };
}
